import { useApiTestingLanguage } from './useApiTestingLanguage';
import React from 'react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Plus, Trash2, Settings } from 'lucide-react';

// Common headers for autocomplete
const commonHeaders = [
  'Content-Type', 'Authorization', 'Accept', 'X-API-Key',
  'Cache-Control', 'User-Agent', 'X-Request-ID'
];

// Common content types for autocomplete
const commonContentTypes = [
  'application/json',
  'application/x-www-form-urlencoded',
  'multipart/form-data',
  'text/plain',
  'text/xml',
  'application/xml'
];

export const HeadersTabContent = ({ testForm, setTestForm }) => {
  const { t } = useApiTestingLanguage();
  const addHeader = (headerName = '', headerValue = '') => {
    setTestForm({
      ...testForm,
      headers: [...testForm.headers, {
        enabled: true,
        key: headerName,
        value: headerValue,
        description: ''
      }]
    });
  };

  const updateHeader = (index, field, value) => {
    const newHeaders = [...testForm.headers];
    newHeaders[index][field] = value;
    setTestForm({ ...testForm, headers: newHeaders });
  };

  const removeHeader = (index) => {
    const newHeaders = testForm.headers.filter((_, i) => i !== index);
    setTestForm({ ...testForm, headers: newHeaders });
  };

  return (
    <>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-sm font-medium text-gray-700">{t("Request Headers")}</h3>
          <p className="text-xs text-gray-500">{t("Add custom headers to your API request")}</p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => addHeader()}
          data-testid="btn-add-header"
        >
          <Plus className="h-3 w-3 mr-1" />
          {t("Add Header")}
        </Button>
      </div>

      {testForm.headers.length === 0 ? (
        <div className="bg-gray-50 rounded-lg p-8 text-center">
          <Settings className="h-8 w-8 mx-auto text-gray-400 mb-3" />
          <p className="text-sm text-gray-500 mb-4">
            {t("No headers added yet. Click \"Add Header\" to add custom headers.")}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {['Content-Type', 'Authorization', 'Accept', 'X-API-Key'].map(headerName => (
              <button
                key={headerName}
                type="button"
                onClick={() => addHeader(headerName, headerName === 'Content-Type' ? 'application/json' : '')}
                className="px-3 py-1.5 text-sm bg-white border border-gray-300 hover:bg-gray-50 rounded-md transition-colors"
              >
                + {headerName}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {/* Header row labels */}
          <div className="grid grid-cols-12 gap-2 text-xs font-medium text-gray-500 px-1 pb-1 border-b">
            <div className="col-span-1 text-center">{t("Active")}</div>
            <div className="col-span-3">{t("Key")}</div>
            <div className="col-span-4">{t("Value")}</div>
            <div className="col-span-3">{t("Description")}</div>
            <div className="col-span-1"></div>
          </div>

          {testForm.headers.map((header, index) => (
            <div key={index} className={`grid grid-cols-12 gap-2 items-center rounded-lg border p-3 transition-colors ${header.enabled ? 'bg-white' : 'bg-gray-50 opacity-60'}`} data-testid={`header-row-${index}`}>
              {/* Enabled checkbox */}
              <div className="col-span-1 flex justify-center">
                <input
                  type="checkbox"
                  checked={header.enabled}
                  onChange={(e) => updateHeader(index, 'enabled', e.target.checked)}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
              </div>

              {/* Key input with autocomplete */}
              <div className="col-span-3">
                <input
                  type="text"
                  placeholder={t("Header name")}
                  value={header.key}
                  onChange={(e) => updateHeader(index, 'key', e.target.value)}
                  list={`header-keys-${index}`}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoComplete="off"
                />
                <datalist id={`header-keys-${index}`}>
                  {commonHeaders.map(h => (
                    <option key={h} value={h} />
                  ))}
                </datalist>
              </div>

              {/* Value input */}
              <div className="col-span-4">
                <input
                  type="text"
                  placeholder={t("Header value")}
                  value={header.value}
                  onChange={(e) => updateHeader(index, 'value', e.target.value)}
                  list={header.key?.toLowerCase() === 'content-type' ? `content-types-${index}` : undefined}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoComplete="off"
                />
                {header.key?.toLowerCase() === 'content-type' && (
                  <datalist id={`content-types-${index}`}>
                    {commonContentTypes.map(ct => (
                      <option key={ct} value={ct} />
                    ))}
                  </datalist>
                )}
              </div>

              {/* Description input */}
              <div className="col-span-3">
                <input
                  type="text"
                  placeholder={t("Description (optional)")}
                  value={header.description || ''}
                  onChange={(e) => updateHeader(index, 'description', e.target.value)}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  autoComplete="off"
                />
              </div>

              {/* Delete button */}
              <div className="col-span-1 flex justify-center">
                <button
                  type="button"
                  onClick={() => removeHeader(index)}
                  className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors"
                  data-testid={`btn-delete-header-${index}`}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}

          {/* Quick add common headers */}
          <div className="mt-4 pt-4 border-t">
            <p className="text-xs text-gray-500 mb-2">{t("Quick add common headers:")}</p>
            <div className="flex flex-wrap gap-2">
              {['Content-Type', 'Authorization', 'Accept', 'X-API-Key', 'Cache-Control', 'User-Agent'].map(headerName => (
                <button
                  key={headerName}
                  type="button"
                  onClick={() => {
                    const exists = testForm.headers.some(h => h.key.toLowerCase() === headerName.toLowerCase());
                    if (!exists) {
                      addHeader(headerName, headerName === 'Content-Type' ? 'application/json' : '');
                    }
                  }}
                  disabled={testForm.headers.some(h => h.key.toLowerCase() === headerName.toLowerCase())}
                  className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed rounded-md transition-colors"
                >
                  + {headerName}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

/**
 * Body Tab - Request body with type selection
 */
export const BodyTabContent = ({ testForm, setTestForm }) => {
  const { t } = useApiTestingLanguage();
  return (
  <div className="space-y-4">
    <div>
      <label className="text-sm font-medium text-gray-700 block mb-2">
        {t("Body Type")}
      </label>
      <select
        value={testForm.body_type}
        onChange={(e) => setTestForm({...testForm, body_type: e.target.value})}
        className="w-full max-w-xs p-2 border border-gray-300 rounded-md"
        data-testid="select-body-type"
      >
        <option value="json">JSON</option>
        <option value="form">{t("Form Data")}</option>
        <option value="raw">{t("Raw Text")}</option>
        <option value="xml">XML</option>
      </select>
    </div>

    <div>
      <label className="text-sm font-medium text-gray-700 block mb-2">
        {t("Request Body")}
        {testForm.method === 'GET' && (
          <span className="ml-2 text-xs text-amber-600">{t("(GET requests typically don't have a body)")}</span>
        )}
      </label>
      <Textarea
        placeholder={testForm.body_type === 'json' ? '{\n  "key": "value"\n}' : t("Enter request body...")}
        value={testForm.body}
        onChange={(e) => setTestForm({...testForm, body: e.target.value})}
        className="font-mono text-sm min-h-[250px]"
        data-testid="input-body"
      />
      <p className="text-xs text-gray-500 mt-2">
        {t("Use")} {'{{variable}}'} {t("syntax to reference environment variables")}
      </p>
    </div>
  </div>
);
};

/**
 * Assertions Tab - Response validation rules
 */
